/** Day-by-day itinerary panel (Lee's decision #2/#5) — opened from
 * PlanBar's ITINERARY button while drawing. Same house sheet pattern as
 * PlansSheet/StatePicker: this owns no day-math (plan/itinerary.ts's
 * buildItinerary does that, off the same `days` PlanBar's own stats row
 * reads), only the sheet chrome, the per-day NOTES textarea, and the
 * PRINT / SAVE PDF trigger — a plain `window.print()`, styled by
 * theme.css's `@media print` block below. Web carries no TripSchedule
 * (that's phone-only), so there are no dates or weather here — just the
 * plan's own geometry, day by day. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { DayStat } from "../plan/daystats";
import { buildItinerary } from "../plan/itinerary";
import { formatDuration, formatGainFt, formatMiles } from "../plan/geo";
import { toUTM, zoneForLongitude } from "../map/utm-grid";
import type { Plan } from "../plan/types";

/** "12  512345E 4657890N" — whole meters, plenty of precision for a
 * hand-copied camp fix. map/utm-grid.ts is northern-hemisphere-only (every
 * Dustlark region is in the continental US — see its own doc comment), so
 * there's no hemisphere letter to fabricate. */
function formatUTM(lon: number, lat: number): string {
  const zone = zoneForLongitude(lon);
  const { easting, northing } = toUTM(lat, lon, zone);
  return `${zone}  ${Math.round(easting)}E ${Math.round(northing)}N`;
}

function formatLatLon(lon: number, lat: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

export default function ItinerarySheet({
  plan,
  days,
  onNotesChange,
  onClose,
}: {
  plan: Plan;
  /** The same per-day slice App.tsx already computes for PlanBar (plan/
   * daystats.ts's dayStats) — read once up there rather than re-derived
   * here, so the two views can never disagree on a day's own numbers. */
  days: DayStat[];
  /** Fires with the FULL notes array (index = day - 1) on every keystroke
   * — App.tsx folds it straight into planner.plan.dayNotes with no undo
   * history, the same treatment the DONE naming field's free text gets. */
  onNotesChange: (notes: string[]) => void;
  onClose: () => void;
}) {
  // Seeded once from the plan's saved notes; this component is the only
  // writer from then on (onNotesChange below), so re-deriving from
  // `plan.dayNotes` on every render would just echo our own last write.
  const [notes, setNotes] = useState<string[]>(() => plan.dayNotes ?? []);
  const itinerary = useMemo(() => buildItinerary(plan, days, notes), [plan, days, notes]);

  // Focus moves in on open and back to whatever had it on close — the same
  // contract PlanBar's own anchor-menus use (see their overflowFirstBtnRef/
  // closuresCloseBtnRef comments), just also restoring afterward since this
  // sheet, unlike those, can be opened from a REAL button elsewhere in the
  // dock rather than a toggle that stays visually in place.
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    return () => previouslyFocused.current?.focus?.();
  }, []);

  // Both setters are dispatched at the top level, never from inside a
  // setNotes updater. React treats updaters as pure and may re-invoke them
  // — StrictMode (main.tsx) does exactly that in dev — so calling the
  // PARENT's setState from in there fired `onNotesChange` twice per
  // keystroke and, whenever an update landed while one was pending, ran it
  // during this component's render: React's "Cannot update a component
  // (App) while rendering a different component (ItinerarySheet)", logged
  // off the feature's main interaction. Reading `notes` from the closure is
  // safe here because this component is its only writer and it re-renders
  // per keystroke.
  const setNote = (day: number, text: string) => {
    const next = [...notes];
    while (next.length < day) next.push("");
    next[day - 1] = text;
    setNotes(next);
    onNotesChange(next);
  };

  return (
    <div className="sheet-backdrop itinerary-backdrop" onClick={onClose}>
      <div
        className="sheet itinerary-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="itinerary-heading"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // stopPropagation is load-bearing, not defensive — same reason
          // as every other sheet's Escape handler in this file family:
          // without it, Escape would also reach App.tsx's window-level
          // draw-mode listener in the same keystroke (deselect a point, or
          // pop "Discard route?"), since ITINERARY is only reachable WHILE
          // drawing.
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="itinerary-head">
          <h2 id="itinerary-heading">Itinerary</h2>
          <button
            className="plan-bar-btn plan-bar-btn-primary itinerary-print-btn"
            onClick={() => window.print()}
            title="Print this itinerary, or save it as a PDF from the print dialog"
          >
            PRINT / SAVE PDF
          </button>
        </div>
        {itinerary.length === 0 ? (
          <p className="itinerary-empty">Draw at least two points to see a day-by-day breakdown.</p>
        ) : (
          <ul className="itinerary-days">
            {itinerary.map((d) => (
              <li key={d.day} className="itinerary-day">
                <div className="itinerary-day-header">
                  <span className="itinerary-day-num">D{d.day}</span>
                  <span className="itinerary-day-route">
                    {d.startLabel} → {d.endLabel}
                  </span>
                </div>
                <div className="itinerary-stats">
                  <span>{formatMiles(d.distanceM)}</span>
                  <span>{d.gainM !== null ? formatGainFt(d.gainM) : "no elevation data"}</span>
                  <span>{d.etaSeconds !== null ? formatDuration(d.etaSeconds) : "no drive estimate"}</span>
                </div>
                {d.campLonLat && (
                  <div className="itinerary-coord">
                    <span>{formatLatLon(d.campLonLat[0], d.campLonLat[1])}</span>
                    <span>{formatUTM(d.campLonLat[0], d.campLonLat[1])}</span>
                  </div>
                )}
                {d.waypoints.length > 0 && (
                  <div className="itinerary-waypoints">{d.waypoints.join(" · ")}</div>
                )}
                <label className="itinerary-notes-label" htmlFor={`itinerary-notes-${d.day}`}>
                  NOTES
                </label>
                <textarea
                  id={`itinerary-notes-${d.day}`}
                  className="itinerary-notes"
                  rows={2}
                  value={d.notes}
                  onChange={(e) => setNote(d.day, e.target.value)}
                  placeholder="What we're doing this day…"
                />
              </li>
            ))}
          </ul>
        )}
        <button
          ref={closeBtnRef}
          className="plan-bar-btn itinerary-close-btn"
          onClick={onClose}
          title="Close itinerary (Esc)"
        >
          CLOSE
        </button>
      </div>
    </div>
  );
}
